import { ITeamAnalyticsActivityEnum, ITeamAnalyticsActivityV2Enum } from '../../enum/team-analytics-activity/team-analytics-activity.enum';
import { IWorkingDaysAnalyticsActivityV2Enum } from '../../enum/working-days-analytics-activity.enum';
import { rotate } from '../../helpers/array.helper';
import { getNumberOfDaysBetweenDates } from '../../helpers/datetime.helper';
import { mapDurationToOpacity } from '../../helpers/heatmaps/heat-map-duration.helper';
import { mapInteractionsToOpacity } from '../../helpers/heatmaps/heat-map-interactions.helper';
import {
  MAX_SEGMENTS,
  SEGMENT_INTERVAL,
} from '../../helpers/heatmaps/heat-map-timestamps.helper';
import { HeatmapIntervalSegment } from '../../interface/heat-maps/heat-map.interface';
import dayjs from 'dayjs';
import dayjsTimezonePlugin from 'dayjs/plugin/timezone';
import dayjsUtcPlugin from 'dayjs/plugin/utc';
dayjs.extend(dayjsUtcPlugin);
dayjs.extend(dayjsTimezonePlugin);

export interface HeatmapPersistenceRecord {
  [IWorkingDaysAnalyticsActivityV2Enum.HeatmapSegment]: number;
  [IWorkingDaysAnalyticsActivityV2Enum.UserId]: string;
  [IWorkingDaysAnalyticsActivityV2Enum.TotalDuration]: string;
  [IWorkingDaysAnalyticsActivityV2Enum.count]: string;
}

export interface HeatmapPersistenceAnalyticsRecord {
  [ITeamAnalyticsActivityEnum.HeatmapSegment]: number;
  [ITeamAnalyticsActivityEnum.UserId]: string;
  [ITeamAnalyticsActivityEnum.TotalDuration]: string;
  [ITeamAnalyticsActivityEnum.Count]: string;
}
export interface HeatmapPersistenceAnalyticsRecordV2 {
  [ITeamAnalyticsActivityV2Enum.HeatmapSegment]: number;
  [ITeamAnalyticsActivityV2Enum.UserId]: string;
  [ITeamAnalyticsActivityV2Enum.TotalDuration]: string;
  [ITeamAnalyticsActivityV2Enum.Count]: string;
}

export interface HeatmapDomainEntityProps {
  heatmapSegment: HeatmapSegmentValueObject;
  userId: string;
  totalDuration: HeatmapSegmentTotalDurationValueObject;
  activityCount: HeatmapSegmentActivityCountValueObject;
}

export interface HeatmapDomainPresenterProps extends HeatmapDomainEntityProps {
  startDate: Date;
  endDate: Date;
  timezone: string;
}

export interface HeatmapSegmentValueObjectProps {
  value: number;
  interval: number;
}

export interface HeatmapSegmentTotalDurationValueObjectProps {
  value: number;
}

export class HeatmapSegmentTotalDurationValueObject {
  value: number;
  private constructor(props: HeatmapSegmentTotalDurationValueObjectProps) {
    this.value = props.value;
  }

  static create(
    props: HeatmapSegmentTotalDurationValueObjectProps
  ): HeatmapSegmentTotalDurationValueObject {
    return new HeatmapSegmentTotalDurationValueObject(props);
  }

  averageAcrossDateRange(start: Date, end: Date): number {
    const numberOfDays = getNumberOfDaysBetweenDates(start, end);
    if (numberOfDays < 1) {
      return this.value;
    }
    return Math.round(this.value / numberOfDays);
  }
}

export interface HeatmapSegmentActivityCountValueObjectProps {
  value: number;
}

export class HeatmapSegmentActivityCountValueObject {
  value: number;
  private constructor(props: HeatmapSegmentActivityCountValueObjectProps) {
    this.value = props.value;
  }

  static create(
    props: HeatmapSegmentActivityCountValueObjectProps
  ): HeatmapSegmentActivityCountValueObject {
    return new HeatmapSegmentActivityCountValueObject(props);
  }

  averageAcrossDateRange(start: Date, end: Date): number {
    const numberOfDays = getNumberOfDaysBetweenDates(start, end);
    if (numberOfDays < 1) {
      return this.value;
    }
    const result = Math.round(100 * (this.value / numberOfDays)) / 100;
    return result
  }
}

export class HeatmapSegmentValueObject {
  value: number;
  interval: number;
  private constructor(props: HeatmapSegmentValueObjectProps) {
    this.value = props.value;
    this.interval = props.interval;
  }
  static create(
    props: HeatmapSegmentValueObjectProps
  ): HeatmapSegmentValueObject {
    return new HeatmapSegmentValueObject(props);
  }

  rotateHeatmapSegment(timezone: string): number {
    const offsetInMinutes = dayjs.utc().tz(timezone).utcOffset();
    const segmentIndex = this.value;
    const segments = [...Array(MAX_SEGMENTS).keys()];
    const rotateLength = Math.floor(offsetInMinutes / this.interval);
    const rotatedSegments = rotate(segments, rotateLength, true);
    return rotatedSegments[segmentIndex];
  }
}

export class HeatmapDomainEntity {
  heatmapSegment: HeatmapSegmentValueObject;
  userId: string;
  totalDuration: HeatmapSegmentTotalDurationValueObject;
  activityCount: HeatmapSegmentActivityCountValueObject;
  dayCount: number;
  private constructor(props: HeatmapDomainEntityProps) {
    this.heatmapSegment = props.heatmapSegment;
    this.userId = props.userId;
    this.totalDuration = props.totalDuration;
    this.activityCount = props.activityCount;
  }
  static create(props: HeatmapDomainEntityProps): HeatmapDomainEntity {
    return new HeatmapDomainEntity(props);
  }

  toJSON(): HeatmapDomainEntityProps {
    return {
      heatmapSegment: this.heatmapSegment,
      userId: this.userId,
      totalDuration: this.totalDuration,
      activityCount: this.activityCount,
    };
  }
}

export class HeatmapReforgeMapper {
  persistenceToDomain(
    record: HeatmapPersistenceRecord | HeatmapPersistenceAnalyticsRecord
  ): HeatmapDomainEntity {
    return HeatmapReforgeMapper.persistenceToDomain(record);
  }
  static persistenceToDomain(
    record: HeatmapPersistenceRecord | HeatmapPersistenceAnalyticsRecord
  ): HeatmapDomainEntity {
    return HeatmapDomainEntity.create({
      heatmapSegment: HeatmapSegmentValueObject.create({
        value: Number(
          record[IWorkingDaysAnalyticsActivityV2Enum.HeatmapSegment] !==
            undefined
            ? record[IWorkingDaysAnalyticsActivityV2Enum.HeatmapSegment]
            : record[ITeamAnalyticsActivityEnum.HeatmapSegment] !== undefined
            ? record[ITeamAnalyticsActivityEnum.HeatmapSegment]
            : undefined
        ),
        interval: SEGMENT_INTERVAL,
      }),
      userId: String(
        record[IWorkingDaysAnalyticsActivityV2Enum.UserId] ||
          record[ITeamAnalyticsActivityEnum.UserId]
      ),
      totalDuration: HeatmapSegmentTotalDurationValueObject.create({
        value: Number(
          record[IWorkingDaysAnalyticsActivityV2Enum.TotalDuration] ||
            record[ITeamAnalyticsActivityEnum.TotalDuration]
        ),
      }),
      activityCount: HeatmapSegmentActivityCountValueObject.create({
        value: Number(
          record[IWorkingDaysAnalyticsActivityV2Enum.count] ||
            record[ITeamAnalyticsActivityEnum.Count]
        ),
      }),
    });
  }

  persistenceToDomainV2(
    record: HeatmapPersistenceRecord | HeatmapPersistenceAnalyticsRecordV2
  ): HeatmapDomainEntity {
    return HeatmapReforgeMapper.persistenceToDomainV2(record);
  }
  static persistenceToDomainV2(
    record: HeatmapPersistenceRecord | HeatmapPersistenceAnalyticsRecordV2
  ): HeatmapDomainEntity {
    return HeatmapDomainEntity.create({
      heatmapSegment: HeatmapSegmentValueObject.create({
        value: Number(
          record[IWorkingDaysAnalyticsActivityV2Enum.HeatmapSegment] !==
            undefined
            ? record[IWorkingDaysAnalyticsActivityV2Enum.HeatmapSegment]
            : record[ITeamAnalyticsActivityV2Enum.HeatmapSegment] !== undefined
            ? record[ITeamAnalyticsActivityV2Enum.HeatmapSegment]
            : undefined
        ),
        interval: SEGMENT_INTERVAL,
      }),
      userId: String(
        record[IWorkingDaysAnalyticsActivityV2Enum.UserId] ||
          record[ITeamAnalyticsActivityV2Enum.UserId]
      ),
      totalDuration: HeatmapSegmentTotalDurationValueObject.create({
        value: Number(
          record[IWorkingDaysAnalyticsActivityV2Enum.TotalDuration] ||
            record[ITeamAnalyticsActivityV2Enum.TotalDuration]
        ),
      }),
      activityCount: HeatmapSegmentActivityCountValueObject.create({
        value: Number(
          record[IWorkingDaysAnalyticsActivityV2Enum.count] ||
            record[ITeamAnalyticsActivityV2Enum.Count]
        ),
      }),
    });
  }

  domainToInterface(entity: HeatmapDomainEntity): HeatmapDomainEntityProps {
    return HeatmapReforgeMapper.domainToInterface(entity);
  }
  static domainToInterface(
    entity: HeatmapDomainEntity
  ): HeatmapDomainEntityProps {
    return entity.toJSON();
  }

  interfaceToPresenter(
    entity: HeatmapDomainPresenterProps
  ): HeatmapIntervalSegment {
    return HeatmapReforgeMapper.interfaceToPresenter(entity);
  }
  static interfaceToPresenter(
    entity: HeatmapDomainPresenterProps
  ): HeatmapIntervalSegment {
    const averageDurations = entity.totalDuration.averageAcrossDateRange(
      entity.startDate,
      entity.endDate
    );
    const averageInteractions = entity.activityCount.averageAcrossDateRange(
      entity.startDate,
      entity.endDate
    );
    return {
      duration: {
        opacity: mapDurationToOpacity(averageDurations),
        value: averageDurations,
      },
      interactions: {
        opacity: mapInteractionsToOpacity(averageInteractions),
        value: averageInteractions,
      },
      localSegment: entity.heatmapSegment.rotateHeatmapSegment(entity.timezone),
    };
  }
}
